import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const idx = c.trim().indexOf('=');
          const name = idx !== -1 ? c.trim().slice(0, idx) : c.trim();
          const value = idx !== -1 ? decodeURIComponent(c.trim().slice(idx + 1)) : '';
          return { name: name.trim(), value };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host}` : ''].filter(Boolean);
  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];
  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage()).find((c) =>
    c.name.includes('auth-token')
  )?.value ?? null;

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const nativeFetch = window.fetch;
  const orig = nativeFetch.bind(window);
  window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
    try {
      const token = getToken();
      const isRequestObj = typeof Request !== 'undefined' && input instanceof Request;
      const url = isRequestObj
        ? (input as Request).url
        : input instanceof URL
          ? input.href
          : (input as string);

      // Normalise headers to a plain object for inspection
      const rawHeaders: Record<string, string> = {};
      if (init?.headers) {
        if (init.headers instanceof Headers) {
          init.headers.forEach((v, k) => { rawHeaders[k] = v; });
        } else if (Array.isArray(init.headers)) {
          (init.headers as string[][]).forEach(([k, v]) => { rawHeaders[k] = v; });
        } else {
          Object.assign(rawHeaders, init.headers);
        }
      }
      if (isRequestObj) {
        (input as Request).headers.forEach((v: string, k: string) => {
          if (!(k in rawHeaders)) rawHeaders[k] = v;
        });
      }

      // Skip token injection for Next.js RSC / navigation requests
      const isRscRequest =
        url.includes('_rsc=') ||
        url.includes('__nextjs') ||
        url.includes('_next/') ||
        'next-router-state-tree' in rawHeaders || 'rsc' in rawHeaders || 'next-router-prefetch' in rawHeaders ||
        rawHeaders['accept']?.includes('text/x-component');

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
      const isSupabaseCall =
        (supabaseUrl.length > 0 && url.startsWith(supabaseUrl)) ||
        url.includes('/rest/v1/') ||
        url.includes('/auth/v1/') ||
        url.includes('/storage/v1/') ||
        url.includes('/realtime/v1/');

      if (token && !isRscRequest && isSupabaseCall) {
        if (isRequestObj) {
          // Clone the Request with merged headers to avoid TypeError
          const mergedHeaders = new Headers((input as Request).headers);
          mergedHeaders.set('x-sb-token', token);
          return orig(new Request(input as Request, { headers: mergedHeaders }));
        } else {
          const mergedInit = {
            ...(init || {}),
            headers: { ...rawHeaders, 'x-sb-token': token },
          };
          return orig(input as string | URL, mergedInit);
        }
      }
    } catch {
      // If token injection fails for any reason, proceed with original request
    }
    // Preserve the original input type — do NOT cast Request to string|URL
    return orig(input as any, init);
  };
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value ? setCookie(name, value, options) : deleteCookie(name)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );
}
